/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Remembered local folders (Sader / Wared) through the File System Access API,
 * available in Chromium browsers (Chrome / Edge) on secure origins. The chosen
 * FileSystemDirectoryHandle is kept in IndexedDB so the operator picks each
 * folder once. Typed loosely (any): the TypeScript DOM lib in use predates
 * these APIs.
 *
 * Pickers and permission prompts only work while handling a user gesture
 * (click), so call chooseFolder / getFolderForUse / pickFiles first thing in
 * a click handler, before any slow request.
 */

export type FolderKey = "sader" | "wared";
export type FolderMode = "read" | "readwrite";

const DB_NAME = "ramz-folders";
const STORE_NAME = "handles";

export function isFolderAccessSupported(): boolean {
  const w = window as any;

  return (
    typeof w.showDirectoryPicker === "function" &&
    typeof w.showOpenFilePicker === "function" &&
    typeof w.indexedDB !== "undefined"
  );
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () =>
      request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function inStore(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest
): Promise<any> {
  const db = await openDb();

  try {
    return await new Promise((resolve, reject) => {
      const request = action(
        db.transaction(STORE_NAME, mode).objectStore(STORE_NAME)
      );

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/** The remembered folder for key, or null when none is set. */
export async function getSavedFolder(key: FolderKey): Promise<any> {
  if (!isFolderAccessSupported()) {
    return null;
  }

  try {
    return (await inStore("readonly", store => store.get(key))) || null;
  } catch (err) {
    console.log("folderAccess: unable to read the saved folder", err);
    return null;
  }
}

/**
 * Lets the operator choose the folder for key and remembers it. Resolves to
 * the handle, or null when the picker was closed.
 */
export async function chooseFolder(
  key: FolderKey,
  mode: FolderMode
): Promise<any> {
  const options: any = { id: `ramz-${key}`, mode: mode };
  const current = await getSavedFolder(key);

  if (current) {
    options.startIn = current;
  }

  let handle: any;

  try {
    handle = await (window as any).showDirectoryPicker(options);
  } catch (err) {
    console.log("folderAccess: no folder chosen", err);
    return null;
  }

  try {
    await inStore("readwrite", store => store.put(handle, key));
  } catch (err) {
    console.log("folderAccess: unable to save the folder", err);
  }

  return handle;
}

/**
 * Makes sure the page may use handle in mode, asking the operator when the
 * browser no longer remembers the permission (e.g. after a restart).
 */
export async function ensurePermission(
  handle: any,
  mode: FolderMode
): Promise<boolean> {
  try {
    if ((await handle.queryPermission({ mode: mode })) === "granted") {
      return true;
    }

    return (await handle.requestPermission({ mode: mode })) === "granted";
  } catch (err) {
    console.log("folderAccess: permission check failed", err);
    return false;
  }
}

/**
 * The remembered folder for key with mode permission, choosing it first when
 * none is set. Null when unsupported, cancelled or denied; callers then fall
 * back to plain downloads.
 */
export async function getFolderForUse(
  key: FolderKey,
  mode: FolderMode
): Promise<any> {
  if (!isFolderAccessSupported()) {
    return null;
  }

  const saved = await getSavedFolder(key);

  if (!saved) {
    // the picker grants the permission it was opened with
    return chooseFolder(key, mode);
  }

  return (await ensurePermission(saved, mode)) ? saved : null;
}

/**
 * Opens the file picker in folder and resolves to the chosen files (empty when
 * the picker was closed). No permission on folder is needed to start there.
 */
export async function pickFiles(folder: any): Promise<File[]> {
  // no picker id on purpose: a remembered id directory could win over startIn
  const options: any = { multiple: true };

  if (folder) {
    options.startIn = folder;
  }

  let handles: any[];

  try {
    handles = await (window as any).showOpenFilePicker(options);
  } catch (err) {
    if (err && err.name === "AbortError") {
      return [];
    }
    throw err;
  }

  return Promise.all(handles.map(handle => handle.getFile()));
}

/** name, or "name (1).ext", "name (2).ext", ... when taken in folder. */
async function freeName(folder: any, name: string): Promise<string> {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.substring(0, dot) : name;
  const ext = dot > 0 ? name.substring(dot) : "";

  for (let i = 0; i < 1000; i++) {
    const candidate = i === 0 ? name : `${stem} (${i})${ext}`;

    try {
      await folder.getFileHandle(candidate);
    } catch (err) {
      if (err && err.name === "NotFoundError") {
        return candidate;
      }
      // TypeMismatchError: a folder has that name, try the next one
      if (!err || err.name !== "TypeMismatchError") {
        throw err;
      }
    }
  }

  throw new Error(`no free file name for ${name}`);
}

/**
 * Writes blob into folder as name, without overwriting existing files.
 * Resolves to the name written, or null when it could not be written.
 */
export async function writeFileToFolder(
  folder: any,
  name: string,
  blob: Blob
): Promise<string | null> {
  let written = "";

  try {
    written = await freeName(folder, name);

    const fileHandle = await folder.getFileHandle(written, { create: true });
    const writable = await fileHandle.createWritable();

    await writable.write(blob);
    await writable.close();

    return written;
  } catch (err) {
    console.log(`folderAccess: unable to write ${name}`, err);

    if (written !== "") {
      folder.removeEntry(written).catch(() => undefined);
    }
    return null;
  }
}

/** Saves blob through a normal browser download. */
export function downloadBlob(blob: Blob, name: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
